import {
  getPremiumModuleRoute,
  SHARP_DETAIL_ROUTE,
} from "@src/features/contentLibrary/utils";
import { locationGroupTypeDisplayName } from "@src/features/home/constants";
import { getUserTypeLabel } from "@src/features/users/edit-user-fields/EditUserType";
import {
  CourseOrModuleItem,
  EmployeeItem,
  JumpToItem,
  LocationItem,
  OrgConfigItem,
  RoleGroupItem,
  RoleItem,
  RouteItem,
  ActionItem,
  SkillItem,
  TagItem,
} from "@src/features/app/ui/jumpToBar/hooks/useAllJumpToItems";
import { LocationGroupType } from "@src/types.generated";
import { formatMultipleGroupNames } from "@src/lib/utils/strings";
import { Route } from "nextjs-routes";
import {
  getLocationDetailRoute,
  getLocationGroupDetailRoute,
  getPersonDetailRoute,
  getRoleDetailRoute,
  getRoleGroupDetailRoute,
  getTagRoute,
} from "@src/features/users/lib/utils";

export const getLinkToItem = (item: JumpToItem): Route => {
  let url = getTagRoute(item.id as string);
  if ("libraryItemId" in item && !item.isSharp && !isOrgConfigItemType(item)) {
    url = {
      pathname: "/library/library-item/[id]",
      query: { id: item.libraryItemId },
    };
  } else if (item.isSharp) {
    url = SHARP_DETAIL_ROUTE;
  } else if (isEmployeeItemType(item)) {
    url = getPersonDetailRoute(item.id as number);
  } else if (isLocationItemType(item)) {
    url = getLocationDetailRoute(item.id as number);
  } else if (isRoleItemType(item)) {
    url = getRoleDetailRoute(item.id as number);
  } else if (isRoleGroupItemType(item)) {
    url = getRoleGroupDetailRoute(item.id as string);
  } else if (isLocationGroupTypeEnum(item.type)) {
    url = getLocationGroupDetailRoute(item.id as string, item.type);
  } else if (isTagItemType(item)) {
    url = getTagRoute(item.id as string);
  } else if (isOrgConfigItemType(item)) {
    url = getPremiumModuleRoute(item.catalogModuleId);
  } else if (isRouteItemType(item)) {
    url = item.route;
  }
  return url;
};

export const itemDescription = (item: JumpToItem): string => {
  if (isEmployeeItemType(item)) {
    if (item.deactivatedAt) {
      return `Deactivated ${getUserTypeLabel(item?.userType)}`;
    }
    return `${getUserTypeLabel(item?.userType)} ${
      item?.locations && item.locations.length > 0
        ? ` at ${formatMultipleGroupNames(item.locations)}`
        : ""
    }`;
  } else if (isLocationGroupTypeEnum(item.type)) {
    return locationGroupTypeDisplayName[item.type];
  } else if (isLocationItemType(item)) {
    return "Location";
  } else if (isRoleItemType(item)) {
    return "Role";
  } else if (isRoleGroupItemType(item)) {
    return "Department";
  } else if (isOrgConfigItemType(item)) {
    return `Premium Module`;
  } else if (isRouteItemType(item)) {
    return "";
  } else if (isActionItemType(item)) {
    return "Staff";
  } else {
    if (item.type === "Skill") {
      return "Check-in";
    } else if (item.type === "SCORMCourse") {
      return "SCORM Course";
    }
    return item.type;
  }
};

export function isContentItemType(
  item: JumpToItem,
): item is CourseOrModuleItem | OrgConfigItem | SkillItem {
  return [
    "Course",
    "PremiumModule",
    "Module",
    "Skill",
    "Task",
    "Resource",
    "SCORMCourse",
  ].includes(item.type);
}

export function isEmployeeItemType(item: JumpToItem): item is EmployeeItem {
  return item.type === "Employee";
}

export function isLocationItemType(item: JumpToItem): item is LocationItem {
  return item.type === "Location";
}

export function isRoleItemType(item: JumpToItem): item is RoleItem {
  return item.type === "Role";
}

export function isRoleGroupItemType(item: JumpToItem): item is RoleGroupItem {
  return item.type === "RoleGroup";
}

export function isTagItemType(item: JumpToItem): item is TagItem {
  return item.type === "Tag";
}

export function isOrgConfigItemType(item: JumpToItem): item is OrgConfigItem {
  return item.type === "PremiumModule";
}

export function isRouteItemType(item: JumpToItem): item is RouteItem {
  return item.type === "route";
}

export function isActionItemType(item: JumpToItem): item is ActionItem {
  return item.type === "action";
}

export const isLocationGroupTypeEnum = (
  type: string,
): type is LocationGroupType => {
  return Object.values(LocationGroupType).includes(type as LocationGroupType);
};

export const getFirstAndLastInitials = (name?: string): string => {
  if (!name) return "??";
  const parts = name.trim().split(" ");
  if (parts.length === 0) return "??";
  const firstInitial = parts[0][0].toUpperCase();
  const lastInitial =
    parts.length > 1 ? parts[parts.length - 1][0].toUpperCase() : "";
  return lastInitial ? `${firstInitial}${lastInitial}` : firstInitial;
};

export const getFirstNameAndLastInitial = (name?: string): string => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  if (parts.length === 0) return "";
  const firstName = parts[0];
  const lastInitial =
    parts.length > 1 ? parts[parts.length - 1][0].toUpperCase() : "";
  return lastInitial ? `${firstName} ${lastInitial}.` : firstName;
};
